import {
    AdmobConsentStatus,
    AdMobError,
    AdMobPlugin,
    AdOptions,
    InterstitialAdPluginEvents,
    RewardAdPluginEvents,
} from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';
import React, { createContext } from 'react';
import * as Sentry from '@sentry/capacitor';
import { PrivacyOptionsRequirementStatus } from '@capacitor-community/admob/dist/esm/consent/privacy-options-requirement-status.enum';

export interface AdInterface {
    initializeIfNeeded(): Promise<void>;
    prepareCompletionInterstitialAdIfNeeded(): Promise<void>;
    showCompletionInterstitialAd(): Promise<void>;
    prepareArchiveUnlockInterstitialAdIfNeeded(): Promise<void>;
    showArchiveUnlockInterstitialAd(): Promise<void>;
    prepareRewardAdIfNeeded(): Promise<void>;
    showRewardAd(): Promise<void>;
    showPrivacyOptions(): Promise<void>;
    adsSupported(): boolean;
    privacyOptionsRequired(): boolean;
}

export function AdInterfaceFactory(): AdInterface {
    if (Capacitor.isNativePlatform()) {
        return new AdNative();
    } else {
        return new AdWeb();
    }
}

export const AdInterfaceContext = createContext<AdInterface | undefined>(undefined);
export function useAdInterfaceContext(): AdInterface {
    const ctx = React.useContext(AdInterfaceContext);
    if (ctx == null) {
        throw new Error('useAdInterfaceContext must be used within an AdInterfaceContextProvider');
    }
    return ctx;
}

export function AdInterfaceContextProvider({ children }: { children: React.ReactNode }) {
    const adInterface = AdInterfaceFactory();
    return <AdInterfaceContext.Provider value={adInterface}>{children}</AdInterfaceContext.Provider>;
}

type AdSenseStatus = 'ok' | 'error' | 'timeout';
export class AdWeb implements AdInterface {
    private readyToShowAd = true;
    private initialized = false;
    private adSenseLoaded?: boolean;

    // Copy-pasted verbatim from Google; treated as an opaque string.
    private ADSENSE_SCRIPT_SRC =
        'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1410468469319248';
    private AD_FREQUENCY_HINT = '30s';
    private LOAD_TIMEOUT_MS = 2500;

    adsSupported(): boolean {
        return true;
    }

    private initGoogleAdsense(): void {
        const script = document.createElement('script');

        script.async = true;
        script.crossOrigin = 'anonymous';
        script.src = this.ADSENSE_SCRIPT_SRC;
        script.setAttribute('data-ad-frequency-hint', this.AD_FREQUENCY_HINT);

        if ((window.ENVIRONMENT_NAME ?? '').toLowerCase() === 'development') {
            script.setAttribute('data-adbreak-test', 'on');
        }

        let settled = false;

        console.info('Initializing Google AdSense...');

        const finish = (status: AdSenseStatus, e?: Error) => {
            console.info('AdSense: finish called with status:', status);
            if (settled) return;
            settled = true;
            cleanup();
            if (status === 'timeout') {
                console.warn('AdSense: script load timed out after ' + this.LOAD_TIMEOUT_MS + 'ms');
            } else if (status === 'error') {
                console.error('AdSense: script failed to load', e);
            }
            this.adSenseLoaded = status === 'ok';
        };

        const onLoad = () => finish('ok');
        const onError = (e) => finish('error', e);

        const timer = setTimeout(() => {
            finish('timeout'); // timeout ⇒ unavailable
        }, this.LOAD_TIMEOUT_MS);

        const cleanup = () => {
            clearTimeout(timer);
            script.removeEventListener('load', onLoad);
            script.removeEventListener('error', onError);
        };

        // Attach listeners first (race-free)
        script.addEventListener('load', onLoad, { once: true });
        script.addEventListener('error', onError, { once: true });
        document.head.appendChild(script);
    }

    initializeIfNeeded(): Promise<void> {
        if (!this.initialized) {
            this.initialized = true;
            console.info('AdWeb: initialize called. Initializing Google AdSense.');
            this.initGoogleAdsense();
        }
        console.info('AdWeb: initialize called. Already initialized.');
        return Promise.resolve();
    }

    prepareCompletionInterstitialAdIfNeeded(): Promise<void> {
        console.info('AdWeb: prepareInterstitialAd called. Initializing if needed.');
        return this.initializeIfNeeded();
    }

    showWebAd(options: Record<string, unknown>): Promise<void> {
        console.info('AdWeb: showWebAd called with options:', options);
        if (!adBreak) {
            console.warn('AdWeb: adBreak function not defined - cannot show interstitial ad');
            return Promise.resolve();
        }
        if (!this.adSenseLoaded) {
            console.warn('AdWeb: Google Ads script not loaded - cannot show interstitial ad');
            return Promise.resolve();
        }
        if (!this.readyToShowAd) {
            console.warn('AdWeb: Ad not ready to be shown yet - skipping ad display');
            return Promise.resolve();
        }
        let resolveFn: (result: {
            breakType: string;
            breakName: string;
            breakFormat: string;
            breakStatus: string;
        }) => void;
        let timeoutId: ReturnType<typeof setTimeout>;
        const promise = new Promise<void>((resolve, reject) => {
            resolveFn = ({ breakType, breakName, breakFormat, breakStatus }) => {
                console.info(
                    `AdWeb: adBreak done: type=${breakType} name=${breakName} format=${breakFormat} status=${breakStatus}`,
                );
                clearTimeout(timeoutId);
                this.readyToShowAd = false;
                setTimeout(() => {
                    this.readyToShowAd = true;
                }, 35000);
                if (breakStatus !== 'viewed') {
                    reject(
                        new Error(
                            'AdWeb: Interstitial ad was not viewed completely. Details: ' +
                                JSON.stringify({ breakType, breakName, breakFormat, breakStatus }),
                        ),
                    );
                    return;
                }
                console.info('AdWeb: resolving showWebAd...');
                resolve();
            };

            // Timeout to detect hung ads that never call adBreakDone
            timeoutId = setTimeout(() => {
                if (!document.querySelector('.adsbygoogle')) {
                    reject(new Error('AdWeb: Ad element not found after 5 seconds - ad failed to load'));
                } else {
                    console.info('AdWeb: Ad element found after 5 seconds - assuming ad loaded');
                }
            }, 5000);
        });
        adBreak({
            adBreakDone: resolveFn!,
            ...options,
        });
        return promise;
    }

    showCompletionInterstitialAd(): Promise<void> {
        return this.showWebAd({
            type: 'next',
            name: 'completion_interstitial',
        });
    }
    prepareArchiveUnlockInterstitialAdIfNeeded(): Promise<void> {
        console.info('AdWeb: prepareArchiveUnlockInterstitialAd called. Initializing if needed.');
        return this.initializeIfNeeded();
    }

    showArchiveUnlockInterstitialAd(): Promise<void> {
        return this.showWebAd({
            type: 'next',
            name: 'archive_unlock_interstitial',
        });
    }

    prepareRewardAdIfNeeded(): Promise<void> {
        console.info('AdWeb: prepareRewardedAd called - no-op');
        return Promise.resolve();
    }
    showRewardAd(): Promise<void> {
        console.info('AdWeb: showRewardedAd called - no-op');
        return Promise.resolve();
    }
    showPrivacyOptions(): Promise<void> {
        console.info('AdWeb: showPrivacyOptions called - no-op');
        return Promise.resolve();
    }
    privacyOptionsRequired(): boolean {
        return false;
    }
}

const TEST_DEVICES = [
    'd11f90902bd4a5ca82d7c98eb084d316', // Mathan's iPhone 16 Pro
];

export class AdNative implements AdInterface {
    private adMob: AdMobPlugin;
    private initializationPromise: Promise<void> | null;
    private rewardAdPromise: Promise<void> | null = null;
    private completionInterstitialAdPromise: Promise<void> | null = null;
    private archiveUnlockInterstitialAdPromise: Promise<void> | null = null;
    private requiresPrivacyOptions: boolean = false;
    private listenersRegistered: boolean = false;

    adsSupported(): boolean {
        return true;
    }

    async initializeIfNeeded(): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            throw new Error('AdNative can only be instantiated on native platforms');
        }
        if (this.initializationPromise != null) {
            console.info('AdNative: Initialization already in progress or completed');
            return this.initializationPromise;
        }

        this.initializationPromise = this._initialize();
        this.initializationPromise.catch((e) => {
            // Null out so we can try again
            console.error('AdNative: _initialize failed, cleaning up for next attempt:', e);
            this.initializationPromise = null;
        });
        return this.initializationPromise;
    }
    private registerAdMobListeners() {
        if (this.listenersRegistered || !this.adMob) return;
        void this.adMob.addListener(RewardAdPluginEvents.FailedToLoad, (err: AdMobError) => {
            console.error('AdMob Reward FailedToLoad:', err);
            Sentry.captureException(err);
        });
        void this.adMob.addListener(RewardAdPluginEvents.FailedToShow, (err: AdMobError) => {
            console.error('AdMob Reward FailedToShow:', err);
            Sentry.captureException(err);
        });
        void this.adMob.addListener(InterstitialAdPluginEvents.FailedToLoad, (err: AdMobError) => {
            console.error('AdMob Interstitial FailedToLoad:', err);
            Sentry.captureException(err);
        });
        void this.adMob.addListener(InterstitialAdPluginEvents.FailedToShow, (err: AdMobError) => {
            console.error('AdMob Interstitial FailedToShow:', err);
            Sentry.captureException(err);
        });
        this.listenersRegistered = true;
    }

    private async _initialize(): Promise<void> {
        console.log('AdNative: Initializing AdMob');
        const { AdMob } = await import('@capacitor-community/admob');
        this.adMob = AdMob;
        await AdMob.initialize({
            initializeForTesting: true,
            testingDevices: TEST_DEVICES,
        });
        this.registerAdMobListeners();

        const [trackingInfo, consentInfo] = await Promise.all([
            AdMob.trackingAuthorizationStatus(),
            AdMob.requestConsentInfo({
                // debugGeography: AdmobConsentDebugGeography.EEA,
                testDeviceIdentifiers: TEST_DEVICES,
            }),
        ]);

        this.requiresPrivacyOptions =
            consentInfo.privacyOptionsRequirementStatus === PrivacyOptionsRequirementStatus.REQUIRED;
        if (consentInfo.isConsentFormAvailable && consentInfo.status === AdmobConsentStatus.REQUIRED) {
            console.info('AdNative: Showing consent form');
            const updatedConsent = await AdMob.showConsentForm();
            const unexpectedConsentState =
                updatedConsent.status === AdmobConsentStatus.REQUIRED ||
                updatedConsent.status !== AdmobConsentStatus.OBTAINED ||
                updatedConsent.canRequestAds === false;
            // We cannot keep pestering the user for ads, we should only ask once.
            // Still, this is unexpected, so log it.
            if (unexpectedConsentState) {
                console.warn('User did not grant consent for ads. ConsentInfo: ' + JSON.stringify(updatedConsent));
                Sentry.captureMessage('User did not grant consent for ads.', {
                    extra: { consentInfo: updatedConsent },
                });
            }
        }

        console.info('AdNative: AdMob initialized');
    }

    async prepareCompletionInterstitialAdIfNeeded(): Promise<void> {
        if (this.completionInterstitialAdPromise != null) {
            console.info('Completion Interstitial ad already prepared or in the process of being prepared');
            return this.completionInterstitialAdPromise;
        }
        const adId = this.getAdIdForCompletion();
        this.completionInterstitialAdPromise = this.prepareInterstitialAd(adId);
        this.completionInterstitialAdPromise.catch((e) => {
            // Null out so we can try again
            console.error('AdNative: prepareCompletionInterstitialAd failed, cleaning up for next attempt:', e);
            this.completionInterstitialAdPromise = null;
        });
        return this.completionInterstitialAdPromise;
    }

    private getAdIdForArchiveUnlock(): string {
        return (
            Capacitor.getPlatform() === 'ios'
                ? process.env.ADMOB_INTERSTITIAL_AD_UNIT_ID_UNLOCK_IOS
                : process.env.ADMOB_INTERSTITIAL_AD_UNIT_ID_UNLOCK_ANDROID
        )!;
    }

    private getAdIdForCompletion(): string {
        return (
            Capacitor.getPlatform() === 'ios'
                ? process.env.ADMOB_INTERSTITIAL_AD_UNIT_ID_COMPLETION_IOS
                : process.env.ADMOB_INTERSTITIAL_AD_UNIT_ID_COMPLETION_ANDROID
        )!;
    }

    async prepareArchiveUnlockInterstitialAdIfNeeded(): Promise<void> {
        if (this.archiveUnlockInterstitialAdPromise != null) {
            console.info('Archive Unlock ad already prepared or in the process of being prepared');
            return this.archiveUnlockInterstitialAdPromise;
        }
        const adId = this.getAdIdForArchiveUnlock();
        this.archiveUnlockInterstitialAdPromise = this.prepareInterstitialAd(adId);
        this.archiveUnlockInterstitialAdPromise.catch((e) => {
            // Null out so we can try again
            console.error('AdNative: prepareArchiveUnlockInterstitialAd failed, cleaning up for next attempt:', e);
            this.archiveUnlockInterstitialAdPromise = null;
        });
        return this.archiveUnlockInterstitialAdPromise;
    }

    private async prepareInterstitialAd(adId: string): Promise<void> {
        await this.initializeIfNeeded();

        console.info('AdNative: Preparing interstitial ad for ad id:', adId);
        const options: AdOptions = {
            adId,
            isTesting: window.ENVIRONMENT_NAME === 'development',
            // npa: true
            // immersiveMode: true
        };
        await this.adMob.prepareInterstitial(options);
        console.info('AdNative: Interstitial ad prepared');
        return;
    }

    async showCompletionInterstitialAd(): Promise<void> {
        try {
            console.info('AdNative: Showing interstitial ad for ad id:', this.getAdIdForCompletion());
            await this.prepareCompletionInterstitialAdIfNeeded();

            await this.showInterstitialAndWaitForDismissal();
        } finally {
            // Clean up so no matter what we prepare a fresh ad next time
            console.info('AdNative: showInterstitialAd: Cleaning up interstitial ad promise');
            this.completionInterstitialAdPromise = null;
        }
        return;
    }

    async showArchiveUnlockInterstitialAd(): Promise<void> {
        try {
            console.info('AdNative: Showing interstitial ad for ad id:', this.getAdIdForArchiveUnlock());
            await this.prepareArchiveUnlockInterstitialAdIfNeeded();
            await this.showInterstitialAndWaitForDismissal();
        } finally {
            // Clean up so no matter what we prepare a fresh ad next time
            console.info('AdNative: showInterstitialAd: Cleaning up interstitial ad promise');
            this.archiveUnlockInterstitialAdPromise = null;
        }
        return;
    }

    async showInterstitialAndWaitForDismissal(): Promise<void> {
        return new Promise((resolve, reject) => {
            const dismissListener = this.adMob.addListener(InterstitialAdPluginEvents.Dismissed, () => {
                // Clean up listener
                void dismissListener.then((handle) => handle.remove());
                resolve();
            });

            // This resolves as soon as the interstitial is shown.
            // We wait for the Dismissed event to resolve the full promise.
            // But, if there is an error showing the ad, we need to reject immediately.
            this.adMob.showInterstitial().catch((err: Error) => {
                // Clean up listener
                void dismissListener.then((handle) => handle.remove());
                reject(err);
            });
        });
    }

    async prepareRewardAdIfNeeded(): Promise<void> {
        if (this.rewardAdPromise != null) {
            console.info('Reward ad already prepared or in the process of being prepared');
            return this.rewardAdPromise;
        }
        this.rewardAdPromise = this.prepareRewardAd();
        this.rewardAdPromise.catch((e) => {
            // Null out so we can try again
            console.error('AdNative: prepareRewardAd failed, cleaning up for next attempt:', e);
            this.rewardAdPromise = null;
        });
        return this.rewardAdPromise;
    }

    private async prepareRewardAd(): Promise<void> {
        await this.initializeIfNeeded();

        console.info('AdNative: Preparing reward ad for ad id:', process.env.ADMOB_REWARD_AD_UNIT_ID);
        const options: AdOptions = {
            adId: process.env.ADMOB_REWARD_AD_UNIT_ID!,
            isTesting: window.ENVIRONMENT_NAME === 'development',
            // npa: true
            // immersiveMode: true
        };
        await this.adMob.prepareRewardVideoAd(options);
        console.info('AdNative: Rewarded ad prepared');
        return;
    }

    async showRewardAd(): Promise<void> {
        try {
            console.info('AdNative: Showing reward ad for ad id:', process.env.ADMOB_REWARD_AD_UNIT_ID);
            await this.prepareRewardAdIfNeeded();
            await this.adMob.showRewardVideoAd();
        } finally {
            // Clean up so no matter what we prepare a fresh ad next time
            console.info('AdNative: showRewardAd: Cleaning up reward ad promise');
            this.rewardAdPromise = null;
        }
        return;
    }

    async showPrivacyOptions(): Promise<void> {
        await this.initializeIfNeeded();

        return this.adMob.showPrivacyOptionsForm();
    }

    privacyOptionsRequired(): boolean {
        return this.requiresPrivacyOptions;
    }
}
